import { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { X } from "lucide-react";
import moment from "moment";

import { useClientSearch } from "features/patients/hooks/useClientSearch";
import { useDebounce } from "hooks/useDebounce";

import styles from "./PatientAutocomplete.module.css";

// `dateOfBirth` is kept, not just rendered into the title. A caller that needs it —
// the vaccine form bounds Date given by it — would otherwise have to fetch the patient
// again to learn something this response already carried, and until that second request
// resolves the bound is simply absent.
const formatOption = (p) => ({
  id: p.id,
  dateOfBirth: p.dateOfBirth,
  title: `${[p.firstName, p.surname].filter(Boolean).join(" ")}${p.dateOfBirth ? ` (${moment(p.dateOfBirth).format("DD-MM-YYYY")})` : ""}`,
});

export const PatientAutocomplete = ({
  value,
  onChange,
  onSearchTermChange,
  placeholder = "Search here...",
  id = "patient-autocomplete",
  className,
  inputClassName,
  inputSx,
  paperSx,
  popperZIndex,
  disabled,
  startAdornment,
  extraInputProps,
  clearOnSelect = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);
  const { data: patients, isFetching } = useClientSearch(debouncedSearch);

  const options = useMemo(
    () => (patients ?? []).map(formatOption),
    [patients]
  );

  const hasPill = !clearOnSelect && !!value && !!value.title;
  const computedInputValue = hasPill ? "" : searchTerm;

  useEffect(() => {
    onSearchTermChange?.(debouncedSearch);
  }, [debouncedSearch, onSearchTermChange]);

  // Clear search term when value is externally reset to null
  useEffect(() => {
    if (!value) {
      setSearchTerm("");
    }
  }, [value]);

  const handleClearPill = () => {
    setSearchTerm("");
    onChange(null);
  };

  return (
    <div className={`${styles.container}${hasPill ? ` ${styles.hasPill}` : ""}`}>
      <Autocomplete
        id={id}
        value={clearOnSelect ? null : value}
        className={`${styles.autocomplete}${className ? ` ${className}` : ""}`}
        disabled={disabled}
        options={options}
        inputValue={computedInputValue}
        onInputChange={(_, val, reason) => {
          if (reason === "input") {
            if (hasPill && val) onChange(null);
            setSearchTerm(val ?? "");
          } else if (reason === "clear") {
            setSearchTerm("");
            onChange(null);
          }
        }}
        getOptionLabel={(opt) => opt?.title ?? ""}
        isOptionEqualToValue={(opt, v) => opt?.id === v?.id}
        filterOptions={(x) => x}
        onChange={(_, newValue) => {
          setSearchTerm("");
          onChange(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className={inputClassName}
            placeholder={placeholder}
            sx={inputSx}
            inputProps={{
              ...params.inputProps,
              ...extraInputProps,
              onClick: (e) => {
                params.inputProps.onClick?.(e);
                if (hasPill) {
                  handleClearPill();
                }
              },
              onKeyDown: (e) => {
                params.inputProps.onKeyDown?.(e);
                if (hasPill && (e.key === "Backspace" || e.key === "Delete")) {
                  e.preventDefault();
                  handleClearPill();
                }
              },
            }}
            InputProps={{
              ...params.InputProps,
              ...(startAdornment && { startAdornment }),
            }}
          />
        )}
        loading={isFetching}
        loadingText="Searching..."
        noOptionsText={
          searchTerm.trim()
            ? "No patients found"
            : "Start typing to search for a patient…"
        }
        disablePortal={false}
        componentsProps={{
          popper: {
            style: { zIndex: popperZIndex ?? 1500, width: 300 },
            modifiers: [
              {
                name: "preventOverflow",
                enabled: true,
                options: { boundary: "viewport" },
              },
            ],
          },
          paper: {
            sx: {
              fontFamily: "Nunito, sans-serif",
              fontSize: "14px",
              borderRadius: "12px",
              marginTop: "4px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
              ...paperSx,
            },
          },
        }}
        slotProps={{
          popper: {
            style: { zIndex: popperZIndex ?? 1500, width: 300 },
            modifiers: [
              {
                name: "preventOverflow",
                enabled: true,
                options: { boundary: "viewport" },
              },
            ],
          },
          paper: {
            sx: {
              fontFamily: "Nunito, sans-serif",
              fontSize: "14px",
              borderRadius: "12px",
              marginTop: "4px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
              ...paperSx,
            },
          },
        }}
      />

      {hasPill && (
        <div className={styles.pillRow}>
          <span
            className={styles.patientPill}
            onClick={handleClearPill}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClearPill();
              }
            }}
            aria-label={`Selected patient: ${value.title}. Click to remove.`}
          >
            <span className={styles.pillLabel}>{value.title}</span>
            <X size={14} strokeWidth={2.5} className={styles.pillDismiss} aria-hidden />
          </span>
        </div>
      )}
    </div>
  );
};
